/**
 * Einstand, Stop und Kurs auf einer gemeinsamen Preisachse.
 *
 * Die Ergänzung zum Kursverlauf darüber, und zwar genau um das, was dieser nicht
 * zeigen kann: Der Einstand liegt in der Regel weit außerhalb der letzten 60
 * Handelstage — bei Apple 98,36 € gegenüber einer Spanne ab 244 €. Er ließe sich
 * nur einzeichnen, indem man die Kurslinie zu einem Strich zusammendrückt.
 *
 * Deshalb eine zweite, eigene Achse. Sie beantwortet die Frage, die beim Setzen
 * eines Stops zählt: Sichert er bereits einen Gewinn, oder begrenzt er noch einen
 * Verlust?
 */

interface PriceAxisProps {
  entryPrice: number;
  stopPrice: number;
  currentPrice: number;
  /** Für die Beschriftung, die Bildschirmleser vorlesen. */
  label: string;
}

export const PriceAxis = ({
  entryPrice,
  stopPrice,
  currentPrice,
  label,
}: PriceAxisProps): React.JSX.Element | null => {
  const low = Math.min(entryPrice, stopPrice, currentPrice);
  const high = Math.max(entryPrice, stopPrice, currentPrice);
  const span = high - low;

  /* Fallen alle drei Kurse zusammen, gibt es keine Strecke zu zeigen. */
  if (span <= 0) return null;

  const at = (price: number): number => ((price - low) / span) * 100;

  /*
   * Ein einziger Balken, nicht zwei: Liegt der Stop unter dem Einstand, läge der
   * Kurs zwischen beiden, und zwei Balken überlappten einander. Der Balken spannt
   * daher immer von Einstand zu Stop — in welcher Richtung auch immer.
   */
  const from = Math.min(at(entryPrice), at(stopPrice));
  const to = Math.max(at(entryPrice), at(stopPrice));

  /* Über dem Einstand sichert der Stop einen Gewinn, darunter begrenzt er einen
   * Verlust. Das ist die Aussage des Balkens, und sie trägt seine Farbe. */
  const securesProfit = stopPrice > entryPrice;

  return (
    <div
      className={`price-axis ${securesProfit ? 'price-axis--profit' : 'price-axis--loss'}`}
      role="img"
      aria-label={label}
    >
      <div className="price-axis__bar" style={{ left: `${from}%`, width: `${to - from}%` }} />
      {/* Gestrichelt wie die Stop-Linie in der Zeichnung darüber: derselbe Kurs,
          dieselbe Schreibweise. */}
      <div className="price-axis__stop" style={{ left: `${at(stopPrice)}%` }} />
      <div className="price-axis__now" style={{ left: `${at(currentPrice)}%` }} />
    </div>
  );
};
